import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CODE_LENGTH, normalizeCodeDraft } from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import { Box, type SxProps, TextField, type Theme, Typography } from '@mui/material';

const { createCodeDialog: strings } = appStrings.modules.rostersAdmin;

type CodeFieldProps = Readonly<{
  /** Prefix for the ids of the label and the input. */
  idPrefix: string;
  code: string;
  onCodeChange: (next: string) => void;
  isError: boolean;
  hint?: string;
}>;

/** A single field sized to a six-character code; input is uppercased and clipped as typed. */
export const CodeField = ({
  idPrefix,
  code,
  onCodeChange,
  isError,
  hint = strings.codeHint,
}: CodeFieldProps) => {
  const inputId = `${idPrefix}-value`;

  return (
    <Box sx={rootSx}>
      <Typography component="label" variant="text-sm-medium" htmlFor={inputId} sx={labelSx}>
        {strings.codeLabel}
      </Typography>
      <TextField
        id={inputId}
        value={code}
        onChange={(event) => onCodeChange(normalizeCodeDraft(event.target.value))}
        inputProps={{
          maxLength: CODE_LENGTH,
          autoCapitalize: 'characters',
          autoComplete: 'off',
          spellCheck: false,
        }}
        sx={fieldSx}
        error={isError}
      />
      <Typography variant="text-sm-regular" sx={hintSx}>
        {hint}
      </Typography>
    </Box>
  );
};

const rootSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

/** Six wide characters plus their tracking; `ch` follows the field's own font. */
const FIELD_WIDTH = `${CODE_LENGTH + 3}ch`;

const fieldSx: SxProps<Theme> = ({ backrs, typography }) => ({
  alignSelf: 'flex-start',
  '& .MuiInputBase-input': {
    ...typography['text-xl-semibold'],
    width: FIELD_WIDTH,
    letterSpacing: '0.2em',
    color: backrs.colors['Colors/Text/text-primary'],
  },
});

const hintSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
